'use client'
import React, { useState } from 'react'
import { useFormik } from 'formik'
import { signInValidationSchema } from '@/src/validations/formvalidations'
import { Button } from '@heroui/react'
import { toast } from '@/src/lib/toast'
import { EyeClosedIcon, EyeOpenIcon, GoogleSVG } from '@/src/ui/AllSVG'
import type { AuthView } from './LoginSignupIndex'
import { useUserLoginMutation } from '@/src/store/rtkQueries/userAuthApi'
import { setAuthCookies, setGeoCookiesFromProfile } from '@/src/utils/authCookies'
import { useDispatch } from 'react-redux'
import { closeModal } from '@/src/store/slices/allModalSlice'
import { closeDrawer } from '@/src/store/slices/allDrawerSlice'
import { useRouter } from 'next/navigation'
import GoogleLogin from './GoogleLogin'
import { EnumErrorCode } from '@/src/utils/constant'
import { IAPIError } from '@/src/store/services/rtkQuerieSetup'
import type { VerifyEmailOtpSource } from './VerifyEmailOtpModal'
import { getFcmTokenCookie } from '@/src/utils/authCookies'

interface SignInProps {
    navigate: (view: AuthView) => void
    onVerifyEmail: (email: string, source: VerifyEmailOtpSource) => void
}

export default function SignIn({ navigate, onVerifyEmail }: SignInProps) {
    const router = useRouter();
    const [showPassword, setShowPassword] = useState(false)
    const dispatch = useDispatch()
    const [userLogin, { isLoading: isSubmitting }] = useUserLoginMutation()
    const { values, errors, touched, handleChange, handleBlur, handleSubmit } = useFormik({
        initialValues: {
            email: '',
            password: '',
            rememberMe: false,
            fcm_token: getFcmTokenCookie(),
        },
        validationSchema: signInValidationSchema,
        onSubmit: async (values) => {
            try {
                const response = await userLogin(values);
                // console.log('response', response);
                if (response?.data?.http_status_code === 200 || response?.data?.http_status_code === 201) {
                    toast.success('Login successful')
                    setAuthCookies({ token: response?.data?.data?.token, membership: response?.data?.data?.membership })
                    setGeoCookiesFromProfile(response?.data?.data?.user?.ip ?? '', response?.data?.data?.user?.country ?? '', response?.data?.data?.user?.iso_code ?? response?.data?.data?.user?.country)
                    router.refresh()
                    dispatch(closeModal())
                    dispatch(closeDrawer())
                } else if ((response?.error as IAPIError)?.data?.message === EnumErrorCode.PLEASE_VERIFY_YOUR_ACCOUNT) {
                    onVerifyEmail(values.email, 'signin-unverified')
                }
            } catch (error) {
                console.error('error', error);
            }
        },
    })

    return (
        <>
            <div className="flex flex-col gap-6 w-full">
                {/* Heading */}
                <div className="space-y-2 text-body-color">
                    <h1 className="secondary_heading">Sign in <span className="font-secondary italic">Now</span></h1>
                    <p className="text-sm">By creating an account, I am also consenting to receive SMS, messages and emails.</p>
                </div>

                {/* Continue with Google */}
                <GoogleLogin />

                {/* Divider */}
                <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-muted-color" />
                    <span className="text-body-color/40">or</span>
                    <div className="flex-1 h-px bg-muted-color" />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Email */}
                    <div className="input_container">
                        <label className="form_label">Email address <span className="required_field">*</span></label>
                        <input
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="example@xyz.com"
                            className="form_input"
                        />
                        {errors.email && touched.email && (
                            <p className="error_text">{errors.email}</p>
                        )}
                    </div>

                    {/* Password */}
                    <div className="input_container">
                        <label className="form_label">Password <span className="required_field">*</span></label>
                        <div className="input_icon_container">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className="form_input"
                            />
                            <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => setShowPassword(p => !p)}
                                className="shrink-0 px-3 text-body-color/40 hover:text-body-color/70 transition-colors"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                            </button>
                        </div>
                        {errors.password && touched.password && (
                            <p className="error_text">{errors.password}</p>
                        )}
                    </div>

                    {/* Remember me + Forgot password */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <input
                                id="rememberMe"
                                type="checkbox"
                                name="rememberMe"
                                checked={values.rememberMe}
                                onChange={handleChange}
                                className="w-4 h-4 accent-body-color cursor-pointer"
                            />
                            <label htmlFor="rememberMe" className="text-sm text-body-color cursor-pointer select-none">
                                Remember me
                            </label>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate('ForgotPassword')}
                            className="text-sm text-body-color transition-colors"
                        >
                            Forgot password?
                        </button>
                    </div>

                    {/* Get OTP */}
                    <div className='space-y-2'>
                        <Button
                            type="button"
                            onClick={() => navigate('SignInEmailStep')}
                            className={`global_btn rounded_full w-full outline_frosted`}
                        >
                            Get an OTP on your email
                        </Button>

                        {/* Submit */}
                        <Button
                            type="submit"
                            className="global_btn rounded_full w-full text-sm font-medium tracking-wide"
                            isDisabled={isSubmitting}
                        >
                            {isSubmitting ? 'Logging in...' : 'Continue'}
                        </Button>
                    </div>
                </form>

                {/* Switch to sign up */}
                <p className="text-sm text-center w-full text-body-color">
                    Didn&apos;t have an account?{' '}
                    <span
                        onClick={() => navigate('SignUp')}
                        className="accent_text"
                    >
                        Sign up
                    </span>
                </p>
            </div>
        </>
    )
}